/**
 * Grupos do AD ligados aos agentes.
 *
 * É a tela que faz o acesso automático existir: a pessoa entra na plataforma,
 * a plataforma vê os grupos dela, e o agente ligado àquele grupo já aparece
 * aberto. Sem chamado, sem aprovação, sem ninguém conceder nada à mão.
 *
 * ## Entra pelo grupo
 *
 * Porque é assim que a operação pensa: existe o `GLOBALGCOPILOT_COMPRAS_SSO`,
 * e a pergunta é "o que ele libera". A tela por agente existiria também, mas
 * responderia a pergunta menos frequente.
 *
 * ## O que esta tela não faz: adivinhar
 *
 * Os nomes dos grupos parecem dizer o agente, e ligar por semelhança seria
 * fácil. **Daria errado**, e os grupos reais do tenant mostram por quê:
 * `GLOBALGCOPILOT_RESUMOCLM_SSO` é do *Criação de Réguas do CLM*, não do
 * *Agente Resumo CLM*, que também existe. `GLOBALGCOPILOT_NORMASCONTROL_SSO`
 * é do *Agente de Contratos*, não da *Consulta de Normas da Controladoria*,
 * que também existe. Dois acertos aparentes, dois acessos concedidos ao agente
 * errado, e nenhum erro visível em lugar nenhum.
 *
 * A busca ajuda a achar. Quem escolhe é quem administra.
 *
 * ## Dois números que a tela insiste em mostrar
 *
 * **Quantas pessoas** o grupo tem, porque ligar é conceder acesso a todas elas
 * de uma vez, e fazer isso sem saber se são três ou trezentas é o tipo de
 * clique que se lamenta depois.
 *
 * **O que muda no agente**, quando ele não é de padrão A. Hoje ele abre para
 * todos com um aviso; ligado a um grupo, passa a exigir o grupo. É mudança de
 * quem entra, e aparece escrita antes de acontecer — nunca como efeito
 * colateral de um botão chamado "ligar".
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, photoUrl, SessionError, goToSignIn } from '../api.js';
import { initialsOf } from './AppShell.jsx';
import { Avatar } from './Avatar.jsx';
import { notify } from './Toast.jsx';
import { PessoasDoGrupo } from './DiretorioPaineis.jsx';
import { AjudaDoTour } from '../tour/AjudaDoTour.jsx';

const FILTROS = [
  { id: 'TODOS', label: 'Todos' },
  { id: 'LIGADOS', label: 'Com agente' },
  { id: 'SOLTOS', label: 'Sem agente' },
];

// Houve uma aba "Vistos no hub", com os grupos que a colheita trouxe e
// ninguém escolheu. Saiu em 21/10: ela servia para DESCOBRIR grupos, e a
// busca no diretório faz isso melhor — acha qualquer um dos 9.712, não só os
// que alguém trouxe no token. A colheita continua rodando, porque é dela que
// vem a contagem de pessoas; o que saiu foi a vitrine dela.

/** Rótulo do padrão de acesso, na linguagem da tela e não do banco. */
const PADRAO = {
  A: { label: 'Por grupo', cls: 'ok' },
  B: { label: 'Abre para todos', cls: 'pending' },
  C: { label: 'Cadastro próprio', cls: 'neutral' },
};

const pessoas = (n) => (n === 1 ? '1 pessoa' : `${n} pessoas`);

/** Quantos rostos cabem na célula antes do `+N`. */
const ROSTOS = 5;

/** `Nome · e-mail`, ou só o e-mail de quem ainda não tem nome gravado. */
const comoSeLe = (p) => (p.name ? `${p.name} · ${p.upn ?? ''}`.trim() : (p.upn ?? p.oid));

/**
 * Os rostos de quem está no grupo.
 *
 * ## São as pessoas que JÁ ENTRARAM, e a tela não finge o contrário
 *
 * `entra_group_member` é alimentado pela resolução de grupos de quem acessa a
 * plataforma. Um grupo com 400 pessoas no Entra das quais 3 usaram o AI Hub
 * mostra 3 — e o `title` do número diz isso, porque a diferença muda a
 * decisão de ligar ou não.
 */
function Rostos({ lista, total }) {
  if (total === 0) return <span className="cell-sub">ninguém ainda</span>;

  const mostrados = lista.slice(0, ROSTOS);
  const restam = total - mostrados.length;
  const nomesQueSobram = lista.slice(ROSTOS).map(comoSeLe);

  return (
    <span className="grupo-rostos">
      {mostrados.map((p) => (
        <span key={p.oid} className="grupo-rosto" title={comoSeLe(p)}>
          <Avatar
            src={p.hasPhoto ? photoUrl.usuario(p.oid) : null}
            initials={initialsOf(p.name, p.upn)}
            name={p.name}
            size="sm"
          />
        </span>
      ))}

      {restam > 0 && (
        /* Os que couberam na consulta vão no `title`; além disso o número
           basta. Buscar todos para um balão seria trazer mil linhas para
           desenhar um "+995". */
        <span
          className="grupo-rosto grupo-rosto-mais"
          title={
            nomesQueSobram.length
              ? nomesQueSobram.join('\n')
              : `mais ${restam} ${restam === 1 ? 'pessoa' : 'pessoas'}`
          }
        >
          +{restam}
        </span>
      )}
    </span>
  );
}

/* ── a janela de ligar ───────────────────────────────────────── */

/**
 * Escolhe o agente e, quando preciso, confirma a troca de padrão.
 *
 * O servidor responde 409 com a explicação quando o item não é de padrão A.
 * A tela **não antecipa** essa decisão a partir do padrão que ela conhece: ela
 * tenta, recebe a resposta e mostra o que o servidor disse. Assim a frase que
 * a pessoa lê vem de quem vai executar a mudança, e não de uma cópia da regra
 * que pode ter envelhecido no navegador.
 */
function Ligar({ grupo, itens, onClose, onPronto }) {
  const [busca, setBusca] = useState('');
  const [alcance, setAlcance] = useState(null);
  const [escolhido, setEscolhido] = useState(null);
  const [confirmacao, setConfirmacao] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    const aoTeclar = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onClose]);

  /**
   * O alcance de verdade, buscado ao abrir a janela.
   *
   * A lista mostra quem já entrou na plataforma com o grupo. **Isso
   * subestima:** um grupo de 400 pessoas das quais 3 usaram o AI Hub aparece
   * como 3, e as 400 ganham o agente conforme entrarem.
   *
   * Aqui é o momento da decisão, então aqui vale a chamada ao Entra — uma, e
   * não uma por linha da lista a cada carregamento da tela.
   */
  useEffect(() => {
    let vivo = true;
    apiFetch(`/api/admin/grupos/${encodeURIComponent(grupo.id)}/alcance`)
      .then((r) => vivo && setAlcance(r))
      .catch(() => vivo && setAlcance({ naPlataforma: grupo.membros, noDiretorio: null }));
    return () => {
      vivo = false;
    };
  }, [grupo.id, grupo.membros]);

  const jaLigados = useMemo(() => new Set(grupo.itens.map((i) => i.id)), [grupo.itens]);

  const visiveis = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return itens
      .filter((i) => i.status !== 'INACTIVE')
      .filter((i) => !t || i.name.toLowerCase().includes(t) || i.id.includes(t))
      .slice(0, 40);
  }, [itens, busca]);

  async function ligar(item, promover) {
    setEnviando(true);
    try {
      await apiFetch(`/api/admin/grupos/${encodeURIComponent(grupo.id)}/itens`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ itemId: item.id, ...(promover ? { promover: true } : {}) }),
      });
      notify(`${item.name} liberado para quem tem este grupo.`);
      onPronto();
      onClose();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();

      // 409 não é erro: é o servidor pedindo a confirmação da troca de padrão.
      // Tratar como erro mandaria a pessoa embora de uma ação que ela pode
      // concluir — e que ela provavelmente quer concluir.
      if (err.status === 409 && err.detail?.motivo === 'precisa_promover') {
        setConfirmacao({ item, message: err.message, ...err.detail });
      } else {
        notify(err.message, 'erro');
      }
    } finally {
      setEnviando(false);
    }
    return undefined;
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="item-panel editor-panel grupo-painel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ligar-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="panel-header editor-head">
          <div>
            <h2 id="ligar-titulo" className="panel-title">
              {grupo.displayName ?? 'Grupo sem nome'}
              <AjudaDoTour tour="alcance" rotulo="O que estes números significam" />
            </h2>
            <p className="panel-desc">
              <span className="grupo-id">{grupo.id}</span>
            </p>
            {/* Os dois números, porque contam coisas diferentes e a diferença
                decide. Nulo é "não consegui saber", nunca zero. */}
            <p className="grupo-alcance-linha" data-tour="grupo-alcance">
              {alcance === null
                ? 'Verificando quantas pessoas…'
                : alcance.noDiretorio === null
                  ? `${pessoas(alcance.naPlataforma)} já entraram no AI Hub com este grupo. Não consegui consultar o tamanho real no Entra.`
                  : `${pessoas(alcance.noDiretorio)} no Entra — destas, ${alcance.naPlataforma} já entraram no AI Hub. As demais recebem o agente conforme entrarem.`}
            </p>
          </div>
          <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <div className="panel-body editor-body">
          {confirmacao ? (
            <div className="grupo-promove">
              <h3>{confirmacao.item.name}</h3>
              <p>{confirmacao.message}</p>
              <p className="grupo-alcance">
                {pessoas(confirmacao.alcance)} da plataforma{' '}
                {confirmacao.alcance === 1 ? 'tem' : 'têm'} este grupo e{' '}
                {confirmacao.alcance === 1 ? 'continuaria' : 'continuariam'} abrindo. Quem não tem
                passa a ver o botão de solicitar.
              </p>
              <div className="panel-actions">
                <button
                  type="button"
                  className="button"
                  disabled={enviando}
                  onClick={() => ligar(confirmacao.item, true)}
                >
                  {enviando ? 'Ligando…' : 'Entendi, ligar assim mesmo'}
                </button>
                <button type="button" className="button ghost" onClick={() => setConfirmacao(null)}>
                  Voltar
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="field-hint">
                Escolha o agente que este grupo libera. O nome do grupo costuma sugerir o agente,
                mas não serve de prova — confira com quem pediu.
              </p>

              <input
                className="input"
                type="search"
                placeholder="Buscar agente pelo nome"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                aria-label="Buscar agente"
              />

              <div className="grupo-escolha">
                {visiveis.length === 0 && (
                  <p className="state-message">Nenhum agente com esse nome.</p>
                )}

                {visiveis.map((i) => {
                  const ligado = jaLigados.has(i.id);
                  const p = PADRAO[i.accessPattern] ?? { label: i.accessPattern, cls: 'neutral' };
                  return (
                    <button
                      key={i.id}
                      type="button"
                      className={`grupo-opcao ${escolhido === i.id ? 'is-active' : ''}`}
                      disabled={ligado || enviando}
                      onClick={() => {
                        setEscolhido(i.id);
                        ligar(i, false);
                      }}
                    >
                      <span className="grupo-opcao-nome">{i.name}</span>
                      <span className={`status-pill ${p.cls}`}>
                        {ligado ? 'Já ligado' : p.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── a aba ───────────────────────────────────────────────────── */

export function AdminGrupos() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('TODOS');
  const [busca, setBusca] = useState('');
  const [ligando, setLigando] = useState(null);
  const [vendoPessoas, setVendoPessoas] = useState(null);
  const [renomeando, setRenomeando] = useState(null); // id do grupo em edição
  const [nomeNovo, setNomeNovo] = useState('');
  const [buscandoNomes, setBuscandoNomes] = useState(false);
  const [doDiretorio, setDoDiretorio] = useState(null); // null = não procurei
  const [procurando, setProcurando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDados(await apiFetch('/api/admin/grupos'));
      setErro(null);
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setErro(err.message);
    } finally {
      setCarregando(false);
    }
    return undefined;
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * Dá nome a um grupo.
   *
   * A claim do token traz identificador e mais nada, e o Graph — que traria o
   * nome — pode recusar. Sem esta porta, uma instância nessa situação ficaria
   * com uma tela de GUIDs e nenhuma saída pela interface.
   */
  async function renomear(grupo) {
    const nome = nomeNovo.trim();
    setRenomeando(null);
    if (nome === (grupo.displayName ?? '')) return;

    try {
      await apiFetch(`/api/admin/grupos/${encodeURIComponent(grupo.id)}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ displayName: nome }),
      });
      carregar();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    }
    return undefined;
  }

  /**
   * Pede os nomes ao Entra.
   *
   * Existe como botão porque a descoberta automática roda quando os grupos de
   * alguém são resolvidos — o que acontece ao abrir o portfólio, não este
   * painel. Quem está aqui olhando GUIDs ficaria esperando um mecanismo que
   * não dispara onde ele está.
   *
   * A resposta é dita por extenso, e distingue os três desfechos: recusado por
   * permissão, nenhum nome encontrado, ou N nomeados. Em silêncio, os dois
   * primeiros parecem iguais e pedem coisas opostas de quem administra.
   */
  async function buscarNomes() {
    setBuscandoNomes(true);
    try {
      const r = await apiFetch('/api/admin/grupos/nomes', { method: 'POST' });

      // Cada desfecho tem frase e ação próprias. A primeira versão juntava
      // "sem token" com "respondeu sem trazer nome", e a tela afirmava que o
      // Entra tinha respondido quando ele nem fora perguntado.
      if (r.motivo === 'sem_token') {
        notify(
          'Não havia um token válido para consultar o Entra — ele não chegou a ser perguntado. Saia e entre de novo, e tente outra vez.',
          'erro',
        );
      } else if (r.motivo === 'erro') {
        notify(
          'A busca falhou no meio do caminho. O log do servidor tem o detalhe; escreva os nomes à mão enquanto isso.',
          'erro',
        );
      } else if (r.motivo === 'recusado') {
        notify(
          `O Entra recusou a consulta (HTTP ${r.status}). Escreva os nomes à mão, ou peça ao IAM a leitura de grupos.`,
          'erro',
        );
      } else if (r.motivo === 'sem_correspondencia') {
        notify(
          r.fonte === 'diretorio'
            ? `O Entra respondeu, mas não reconheceu nenhum dos ${r.faltavam} identificadores. Costumam ser grupos apagados do diretório depois de alguém tê-los no token.`
            : `O Entra só respondeu sobre os seus próprios grupos, e nenhum deles é dos ${r.faltavam} sem nome aqui. Com a permissão de leitura de diretório, a busca resolveria todos de uma vez.`,
          'erro',
        );
      } else if (r.faltavam === 0) {
        notify('Todos os grupos já têm nome.');
      } else {
        notify(
          r.fonte === 'meus_grupos'
            ? `${r.nomeados} de ${r.faltavam} com nome — só os seus. A permissão de leitura de diretório resolveria o resto.`
            : `${r.nomeados} grupo${r.nomeados === 1 ? '' : 's'} com nome agora.`,
        );
        carregar();
      }
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    } finally {
      setBuscandoNomes(false);
    }
    return undefined;
  }

  /**
   * Devolve um item ao acesso aberto.
   *
   * É o inverso da promoção, e faltava: ligar o primeiro grupo fecha o item, e
   * até 19/10 não havia botão nenhum para reabrir — só SQL. Pede confirmação
   * porque reabre para a organização inteira, que é o oposto do que a outra
   * metade desta tela faz.
   */
  async function reabrir(item) {
    const certeza = window.confirm(
      `Reabrir "${item.name}" para todos?

` +
        `Ele deixa de depender de grupo e volta a aparecer liberado para qualquer pessoa, ` +
        `com o aviso de que o acesso pode depender de licença na ferramenta. ` +
        `${item.grupos === 1 ? 'O vínculo com 1 grupo será desfeito' : `Os vínculos com ${item.grupos} grupos serão desfeitos`}.`,
    );
    if (!certeza) return;

    try {
      await apiFetch(`/api/admin/grupos/itens/${encodeURIComponent(item.id)}/reabrir`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ padrao: 'B' }),
      });
      notify(`${item.name} voltou a abrir para todos.`);
      carregar();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    }
    return undefined;
  }

  /**
   * Procura no diretório do Entra o que a lista não tem.
   *
   * A lista desta tela só cresce quando alguém **que tem o grupo** entra na
   * plataforma. Para ligar um agente a um grupo cujos membros ainda não usaram
   * o AI Hub, era preciso colar o GUID do portal — isto resolve.
   */
  async function procurar() {
    const q = busca.trim();
    if (q.length < 2) return;

    setProcurando(true);
    try {
      const r = await apiFetch(`/api/admin/grupos/diretorio?q=${encodeURIComponent(q)}`);
      setDoDiretorio(r);
      if (r.motivo === 'recusado') {
        notify(`O Entra recusou a busca (HTTP ${r.status}).`, 'erro');
      } else if (r.motivo === 'sem_token') {
        notify('Sem token válido para consultar o Entra. Saia e entre de novo.', 'erro');
      }
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    } finally {
      setProcurando(false);
    }
    return undefined;
  }

  /** Tira da lista. O grupo continua no Entra e continua contando pessoas. */
  async function tirar(g) {
    try {
      await apiFetch(`/api/admin/grupos/${encodeURIComponent(g.id)}/lista`, { method: 'DELETE' });
      notify(`${g.displayName ?? 'O grupo'} saiu da lista.`);
      carregar();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    }
    return undefined;
  }

  /** Traz um grupo para a lista — do diretório ou dos colhidos. */
  async function trazer(g) {
    try {
      await apiFetch(`/api/admin/grupos/${encodeURIComponent(g.id)}/lista`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(g.displayName ? { displayName: g.displayName } : {}),
      });
      notify(`${g.displayName} entrou na lista.`);
      setDoDiretorio((atual) =>
        atual ? { ...atual, grupos: atual.grupos.filter((x) => x.id !== g.id) } : atual,
      );
      carregar();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    }
    return undefined;
  }

  async function desligar(grupo, item) {
    try {
      await apiFetch(
        `/api/admin/grupos/${encodeURIComponent(grupo.id)}/itens/${encodeURIComponent(item.id)}`,
        { method: 'DELETE' },
      );
      notify(`${item.name} não é mais liberado por este grupo.`);
      carregar();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    }
    return undefined;
  }

  // A lista gerenciada: o que a administração escolheu, mais os ligados que a
  // plataforma nunca viu (que são gerenciados por definição).
  const todos = useMemo(() => [...(dados?.grupos ?? []), ...(dados?.orfaos ?? [])], [dados]);

  const visiveis = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return todos
      .filter((g) => {
        if (filtro === 'LIGADOS') return g.itens.length > 0;
        if (filtro === 'SOLTOS') return g.itens.length === 0;
        return true;
      })
      .filter((g) => !t || (g.displayName ?? '').toLowerCase().includes(t) || g.id.includes(t));
  }, [todos, filtro, busca]);

  const semNome = useMemo(() => todos.filter((g) => !g.displayName).length, [todos]);

  const controlados = dados?.controlados ?? [];

  const diag = dados?.diagnostico;
  const itens = dados?.itens ?? [];

  return (
    <div className="list-panel">
      {/*
        O diagnóstico só aparece quando explica alguma coisa. Com grupos na
        lista e vínculos feitos, ele seria ruído numa tela que já está dizendo
        o que importa.
      */}
      {/* A lista vazia agora significa "ninguém escolheu ainda", e não
          "ninguém entrou" — a curadoria mudou o sentido do zero. */}
      {diag && diag.grupos === 0 && (
        <p className="grupo-diag" role="status">
          Nenhum grupo na lista ainda. <strong>Procure pelo nome</strong> no campo de busca — a
          plataforma acha qualquer grupo do Entra e traz para cá o que você escolher.
        </p>
      )}

      {diag && diag.grupos > 0 && diag.vinculos === 0 && (
        <p className="grupo-diag" role="status">
          {diag.grupos} grupo{diag.grupos === 1 ? '' : 's'} conhecido
          {diag.grupos === 1 ? '' : 's'}, nenhum ligado a um agente ainda. Enquanto não houver
          vínculo, nenhum agente é liberado por grupo.
        </p>
      )}

      {/*
        Só aparece quando a maioria está sem nome, que é o sintoma de a claim
        do token ter resolvido sozinha — ela traz identificador e nada mais.
        Um ou outro sem nome é normal e não merece aviso.
      */}
      {semNome > 0 && semNome >= todos.length / 2 && (
        <p className="grupo-diag" role="status">
          {semNome} de {todos.length} grupos estão sem nome. Os grupos chegam pelo token, que
          carrega só o identificador — o nome vem de uma consulta ao Entra, que acontece sozinha
          conforme as pessoas usam a plataforma.{' '}
          <strong>Buscar nomes no Entra resolve todos de uma vez.</strong> Se a consulta for
          recusada, clique no nome para escrevê-lo à mão, conferindo no portal do Entra qual grupo é
          cada identificador antes de ligar a um agente.
          <span className="grupo-diag-acao">
            <button
              type="button"
              className="button secondary"
              disabled={buscandoNomes}
              onClick={buscarNomes}
            >
              {buscandoNomes ? 'Buscando…' : 'Buscar nomes no Entra'}
            </button>
          </span>
        </p>
      )}

      {/*
        O que está fechado agora, e o caminho de volta.
        Fica no topo porque é a consequência do que se faz aqui embaixo — e
        porque um agente restrito por engano não pode depender de alguém
        lembrar de conferir o catálogo item a item.
      */}
      {controlados.length > 0 && (
        <div className="grupo-fechados">
          <h3>Controlados por grupo</h3>
          <p className="field-hint">
            Estes só abrem para quem tem um dos grupos ligados. Os outros itens do catálogo
            continuam abrindo para todos.
          </p>
          <ul>
            {controlados.map((i) => (
              <li key={i.id}>
                <span className="grupo-fechado-nome">{i.name}</span>
                <span className="cell-sub">
                  {i.grupos === 1 ? '1 grupo' : `${i.grupos} grupos`} ·{' '}
                  {i.alcance === 0 ? (
                    // Zero é o caso que mais assusta e menos aparece: há grupo
                    // ligado e ninguém que entrou o tem. O item está fechado
                    // para todo mundo, e nada na tela do catálogo diria isso.
                    <strong>ninguém que já entrou consegue abrir</strong>
                  ) : (
                    `${pessoas(i.alcance)} ${i.alcance === 1 ? 'consegue' : 'conseguem'} abrir`
                  )}
                </span>
                <button type="button" className="button ghost" onClick={() => reabrir(i)}>
                  Reabrir para todos
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="tabs-row">
        <div className="tabs" role="tablist" aria-label="Filtrar grupos">
          {FILTROS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filtro === f.id}
              className={`tab ${filtro === f.id ? 'is-active' : ''}`}
              onClick={() => setFiltro(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="tabs-actions">
          <input
            className="input search-box"
            type="search"
            placeholder="Buscar grupo"
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              // O resultado do diretório é de outro termo assim que se digita.
              setDoDiretorio(null);
            }}
            aria-label="Buscar grupo"
          />
        </div>
      </div>

      {carregando && <p className="state-message">Carregando…</p>}

      {erro && (
        <div className="state-message error" role="alert">
          <p>{erro}</p>
          <button className="button secondary" type="button" onClick={carregar}>
            Tentar de novo
          </button>
        </div>
      )}

      {!carregando && !erro && visiveis.length === 0 && (
        <p className="state-message">
          {busca.trim() ? 'Nenhum grupo com esse nome.' : 'Nenhum grupo nesta situação.'}
        </p>
      )}

      {/*
        A ponte para o diretório.
        A lista acima só tem grupos que a plataforma VIU — alguém que os tem
        precisou entrar. Para ligar um agente a um grupo cujos membros ainda
        não usaram o AI Hub, este é o caminho; antes era colar o GUID do
        portal do Entra.
      */}
      {!carregando && !erro && busca.trim().length >= 2 && (
        <div className="grupo-diretorio">
          <p className="field-hint">
            {visiveis.length === 0
              ? 'Esse grupo pode existir no Entra sem ninguém dele ter entrado aqui ainda.'
              : 'Procurando outro? O Entra tem grupos que esta lista ainda não viu.'}
          </p>

          <button
            type="button"
            className="button secondary"
            disabled={procurando}
            onClick={procurar}
          >
            {procurando ? 'Procurando…' : `Procurar "${busca.trim()}" no Entra`}
          </button>

          {doDiretorio?.motivo === 'ok' && doDiretorio.grupos.length === 0 && (
            <p className="state-message">
              O Entra não tem nenhum grupo com esse nome — ou todos já estão na lista acima.
            </p>
          )}

          {doDiretorio?.grupos?.length > 0 && (
            <ul className="grupo-achados">
              {doDiretorio.grupos.map((g) => (
                <li key={g.id}>
                  <span className="grupo-quem">
                    <span className="grupo-fechado-nome">{g.displayName}</span>
                    <span className="cell-sub grupo-id">{g.id}</span>
                  </span>
                  <button type="button" className="button ghost" onClick={() => trazer(g)}>
                    Trazer para a lista
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!carregando && !erro && visiveis.length > 0 && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Grupo</th>
                <th className="num">Pessoas</th>
                <th>Libera</th>
                <th className="col-action" />
              </tr>
            </thead>
            <tbody>
              {visiveis.map((g) => (
                <tr key={g.id}>
                  <td>
                    {renomeando === g.id ? (
                      <input
                        className="input grupo-nome-campo"
                        autoFocus
                        value={nomeNovo}
                        maxLength={256}
                        aria-label="Nome do grupo"
                        placeholder="GLOBALGCOPILOT_…"
                        onChange={(e) => setNomeNovo(e.target.value)}
                        /* Sai salvando. Perder o que se digitou por clicar
                           fora é o jeito mais rápido de alguém desistir de
                           nomear 26 grupos. */
                        onBlur={() => renomear(g)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') renomear(g);
                          if (e.key === 'Escape') setRenomeando(null);
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className={`grupo-nome ${g.displayName ? '' : 'is-vazio'}`}
                        title="Clique para dar nome"
                        onClick={() => {
                          setNomeNovo(g.displayName ?? '');
                          setRenomeando(g.id);
                        }}
                      >
                        {g.displayName ?? 'Grupo sem nome'}
                      </button>
                    )}
                    <div className="cell-sub grupo-id">{g.id}</div>
                  </td>
                  <td className="num">
                    {/* O número conta TODOS; a fileira mostra os primeiros.
                        O `title` diz de onde vem o número, porque "3" sobre um
                        grupo de 400 pessoas no Entra é uma meia-verdade que
                        muda a decisão de ligar. */}
                    <button
                      type="button"
                      className="grupo-pessoas grupo-pessoas-botao"
                      onClick={() => setVendoPessoas(g)}
                      title={`${pessoas(g.membros)} com este grupo já entraram no AI Hub. Clique para ver todas as pessoas do grupo no Entra.`}
                    >
                      <Rostos lista={g.pessoas ?? []} total={g.membros} />
                    </button>
                  </td>
                  <td>
                    {g.itens.length === 0 ? (
                      <span className="cell-sub">nenhum agente</span>
                    ) : (
                      <div className="grupo-itens">
                        {g.itens.map((i) => (
                          <span key={i.id} className="grupo-chip">
                            {i.name}
                            <button
                              type="button"
                              className="grupo-chip-x"
                              aria-label={`Desligar ${i.name} deste grupo`}
                              title="Desligar"
                              onClick={() => desligar(g, i)}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="col-action">
                    <button
                      type="button"
                      className="button secondary"
                      onClick={() => setLigando(g)}
                    >
                      Ligar agente
                    </button>
                    {g.itens.length === 0 && (
                      /* Só sem agente ligado: com vínculo, tirar da lista
                         deixaria um acesso concedido e invisível, e o servidor
                         recusa com 409. */
                      <button
                        type="button"
                        className="button ghost grupo-tirar"
                        title="Tira da lista; o grupo continua no Entra"
                        onClick={() => tirar(g)}
                      >
                        Tirar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ligando && (
        <Ligar grupo={ligando} itens={itens} onClose={() => setLigando(null)} onPronto={carregar} />
      )}

      {vendoPessoas && (
        <PessoasDoGrupo grupo={vendoPessoas} onClose={() => setVendoPessoas(null)} />
      )}
    </div>
  );
}

export default AdminGrupos;
